import { CommonModule } from '@angular/common';
import { Component, DestroyRef, HostListener, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiKeyRecord, CreatedApiKeyRecord, SiteSummary } from '../../models/cms-models';
import { ApiKeysService } from '../../services/api-keys-service';
import { SiteService } from '../../services/site-service';
import { SiteContextService } from '../../services/site-context.service';

@Component({
  selector: 'app-site-detail-page',
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './site-detail-page.html',
  styleUrl: './site-detail-page.css',
})
export class SiteDetailPage implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private siteService = inject(SiteService);
  private apiKeysService = inject(ApiKeysService);
  private siteContext = inject(SiteContextService);

  site: SiteSummary | null = null;
  apiKeys: ApiKeyRecord[] = [];
  draftName = '';
  draftKeyName = '';
  createdKeyDialog: CreatedApiKeyRecord | null = null;
  copyMessage = '';
  loading = true;
  savingName = false;
  savingKey = false;
  deletingSite = false;
  error: string | null = null;
  private loadedSiteId: string | null = null;

  async ngOnInit(): Promise<void> {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const siteId = params.get('siteId');

      if (!siteId || siteId === this.loadedSiteId) {
        return;
      }

      void this.loadSiteWorkspace(siteId);
    });
  }

  async saveSiteName(): Promise<void> {
    if (!this.site) {
      return;
    }

    const trimmedName = this.draftName.trim();
    if (!trimmedName || trimmedName === this.site.name) {
      return;
    }

    this.savingName = true;
    this.error = null;

    try {
      const updated = await this.siteService.updateSite(this.site.id, trimmedName);
      this.site = updated;
      this.draftName = updated.name;
      this.siteContext.upsertSite(updated, { setActive: this.siteContext.activeSiteId === updated.id });
    } catch {
      this.error = 'Failed to update the site name.';
    } finally {
      this.savingName = false;
    }
  }

  async createKey(): Promise<void> {
    if (!this.site || !this.draftKeyName.trim()) {
      return;
    }

    this.savingKey = true;
    this.error = null;

    try {
      const created = await this.apiKeysService.createApiKey(this.site.id, this.draftKeyName.trim());
      this.apiKeys = [
        {
          id: created.id,
          name: created.name,
          siteId: created.siteId,
          keyPreview: created.keyPreview,
        },
        ...this.apiKeys,
      ];
      this.site = {
        ...this.site,
        publicApiKeyCount: this.site.publicApiKeyCount + 1,
      };
      this.siteContext.upsertSite(this.site);
      this.createdKeyDialog = created;
      this.copyMessage = '';
      this.draftKeyName = '';
    } catch {
      this.error = 'Failed to create API key.';
    } finally {
      this.savingKey = false;
    }
  }

  async deleteKey(apiKey: ApiKeyRecord): Promise<void> {
    if (!this.site) {
      return;
    }

    const confirmed = window.confirm(`Delete "${apiKey.name}" for ${this.site.name}? This cannot be undone.`);
    if (!confirmed) {
      return;
    }

    try {
      await this.apiKeysService.deleteApiKey(apiKey.id);
      this.apiKeys = this.apiKeys.filter((existing) => existing.id !== apiKey.id);
      if (this.createdKeyDialog?.id === apiKey.id) {
        this.closeCreatedKeyDialog();
      }
      this.site = {
        ...this.site,
        publicApiKeyCount: Math.max(0, this.site.publicApiKeyCount - 1),
      };
      this.siteContext.upsertSite(this.site);
      this.error = null;
    } catch {
      this.error = 'Failed to delete API key.';
    }
  }

  async deleteSite(): Promise<void> {
    if (!this.site) {
      return;
    }

    const confirmed = window.confirm(`Delete "${this.site.name}"? This cannot be undone.`);
    if (!confirmed) {
      return;
    }

    this.deletingSite = true;
    this.error = null;

    try {
      await this.siteService.deleteSite(this.site.id);
      this.siteContext.removeSite(this.site.id);
      await this.router.navigate(['/sites']);
    } catch {
      this.error = 'Failed to delete site.';
    } finally {
      this.deletingSite = false;
    }
  }

  async copyCreatedKey(): Promise<void> {
    if (!this.createdKeyDialog) {
      return;
    }

    try {
      await navigator.clipboard.writeText(this.createdKeyDialog.key);
      this.copyMessage = 'API key copied to clipboard.';
    } catch {
      this.copyMessage = 'Unable to copy automatically. Copy the key manually.';
    }
  }

  closeCreatedKeyDialog(): void {
    this.createdKeyDialog = null;
    this.copyMessage = '';
  }

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    this.closeCreatedKeyDialog();
  }

  async openDashboard(): Promise<void> {
    if (!this.site) {
      return;
    }

    this.siteContext.setActiveSite(this.site.id);
    await this.router.navigate(['/']);
  }

  async openLandingPages(): Promise<void> {
    if (!this.site) {
      return;
    }

    this.siteContext.setActiveSite(this.site.id);
    await this.router.navigate(['/landing-pages']);
  }

  async openLinks(): Promise<void> {
    if (!this.site) {
      return;
    }

    this.siteContext.setActiveSite(this.site.id);
    await this.router.navigate(['/links']);
  }

  async openImages(): Promise<void> {
    if (!this.site) {
      return;
    }

    this.siteContext.setActiveSite(this.site.id);
    await this.router.navigate(['/images']);
  }

  async openGalleries(): Promise<void> {
    if (!this.site) {
      return;
    }

    this.siteContext.setActiveSite(this.site.id);
    await this.router.navigate(['/galleries']);
  }

  private async loadSiteWorkspace(siteId: string): Promise<void> {
    this.loading = true;
    this.error = null;
    this.closeCreatedKeyDialog();

    try {
      const [site, apiKeys] = await Promise.all([
        this.siteService.getSite(siteId),
        this.apiKeysService.getSiteApiKeys(siteId),
      ]);

      this.site = site;
      this.apiKeys = apiKeys;
      this.draftName = site.name;
      this.loadedSiteId = siteId;
      this.siteContext.upsertSite(site, { setActive: true });
    } catch {
      this.site = null;
      this.apiKeys = [];
      this.draftName = '';
      this.loadedSiteId = null;
      this.error = 'Failed to load the selected site.';
    } finally {
      this.loading = false;
    }
  }
}
